import * as Sentry from "@sentry/react";
import { apiFetch, getEffectiveSession } from "@common/lib/api";
import { getStoredSelectedClinicAccountId } from "@common/lib/selectedClinicAccountStorage";
import { getHttpStatus } from "./accounts";

/** EN-1320: the clinic's Cornerstone auto-finalize setting. `days` is null until first saved. */
export type CornerstoneAutoFinalize = { enabled: boolean; days: number | null };

const PATH = "integration/cornerstone/auto-finalize";

export const cornerstoneAutoFinalizeKey = (accountId: string | undefined) =>
    ["cornerstone-auto-finalize", accountId] as const;

/**
 * Reads the setting for `accountId` (sent as `account_id`, like the PUT), so the query key and the request
 * name the same clinic. 404 means it is not a HappyBridge Cornerstone clinic: resolves null. Other errors throw.
 */
export async function fetchCornerstoneAutoFinalize(accountId: string): Promise<CornerstoneAutoFinalize | null> {
    try {
        const data = await apiFetch<CornerstoneAutoFinalize>(PATH, {
            method: "GET",
            searchParams: { account_id: accountId },
            skipSentryOn404: true,
        });
        return data ?? null;
    } catch (e) {
        if (getHttpStatus(e) === 404) return null;
        throw e;
    }
}

/** The selected clinic changed after the save was made: nothing was sent. */
export class ClinicChangedError extends Error {
    constructor() {
        super("Selected clinic changed before the save was sent");
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = "ClinicChangedError";
    }
}

/**
 * `accountId` is the clinic the save was made on. It goes in the body as `account_id`, which the API
 * prefers over the X-HappyDoc-Account-Id header when the user is an active member of that clinic (if not,
 * the API falls back to its default clinic resolution, not to the header). The guard below drops a save
 * made for a clinic that is no longer selected (debounced or queued past a clinic switch). It is best
 * effort: a switch in another tab after this check still sends the save, but to the clinic it was made
 * on, never the newly selected one. With no stored clinic there is nothing to compare.
 * apiFetch reports failed requests to Sentry; only the empty-body case below is reported here.
 */
export async function saveCornerstoneAutoFinalize(
    s: CornerstoneAutoFinalize,
    accountId: string,
): Promise<CornerstoneAutoFinalize> {
    const session = await getEffectiveSession();
    const storedClinic = session ? getStoredSelectedClinicAccountId(session.userId) : null;
    if (storedClinic !== null && storedClinic !== accountId) throw new ClinicChangedError();
    const data = await apiFetch<CornerstoneAutoFinalize>(PATH, {
        method: "PUT",
        json: { ...s, account_id: accountId },
    });
    if (!data) {
        const error = new Error(`Empty response from ${PATH}`);
        Sentry.captureException(error, { tags: { errorCategory: "code_bug", endpoint: PATH } });
        throw error;
    }
    return data;
}
